import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import i18n from "@/i18n";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";

export interface ProductSubtypeRow {
  id: string;
  name: string;
  customer_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductSubtypeInsert {
  id?: string;
  name: string;
  customer_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface UseProductSubtypesOptions {
  enabled?: boolean;
}

export const useProductSubtypes = ({ enabled = true }: UseProductSubtypesOptions = {}) => {
  const { data: customerId } = useCurrentClinicCustomerId();

  return useQuery({
    queryKey: ["product-subtypes", customerId],
    enabled: enabled && !!customerId,
    queryFn: async () => {
      if (!customerId) throw new Error("Nenhuma clínica selecionada");
      const { data, error } = await supabase
        .from("product_subtypes")
        .select("*")
        .order("name", { ascending: true })
        .or(`customer_id.is.null,customer_id.eq.${customerId}`);

      if (error) throw error;
      return (data ?? []) as ProductSubtypeRow[];
    },
  });
};

export const useCreateProductSubtype = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: Omit<ProductSubtypeInsert, "customer_id">) => {
      const customerId = await getCurrentClinicCustomerId();
      const { data, error } = await supabase
        .from("product_subtypes")
        .insert({
          name: payload.name.trim(),
          customer_id: customerId,
        })
        .select()
        .single();

      if (error) throw error;
      return data as ProductSubtypeRow;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-subtypes"] });
      toast.success(i18n.t("erp:itemForm.groupToasts.created"));
    },
    onError: (error) => {
      console.error("Erro ao cadastrar subtipo:", error);
      toast.error(i18n.t("erp:itemForm.groupToasts.createError"));
    },
  });
};

/** Fixed UUID for default "vacina" subtype (used by useVaccines). */
export const DEFAULT_VACCINE_SUBTYPE_ID = "a1b2c3d4-0003-4000-8000-000000000003";
